"use server"

import { sdk } from "@lib/config"
import { getAuthHeaders, getCacheOptions } from "@lib/data/cookies"
import { getRegion } from "@lib/data/regions"
import { HttpTypes } from "@medusajs/types"
import { revalidateTag } from "next/cache"
import { getLocale } from "next-intl/server"

/**
 * Search for products by query term across all translation locales.
 *
 * Uses a two-step approach:
 * 1. Cross-language search endpoint to find product IDs matching in any locale
 * 2. Fetch full products by IDs (with enrichment: rental pricing, calculated prices)
 *
 * This allows searching in Russian and seeing results in English, etc.
 */
export async function searchProducts({
  query,
  countryCode,
  limit = 12,
}: {
  query: string
  countryCode: string
  limit?: number
}): Promise<{
  products: HttpTypes.StoreProduct[]
  count: number
}> {
  if (!query || query.trim().length === 0) {
    return { products: [], count: 0 }
  }

  const region = await getRegion(countryCode)

  if (!region) {
    return { products: [], count: 0 }
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  const next = {
    ...(await getCacheOptions("products")),
  }

  const locale = await getLocale()

  try {
    // Step 1: Cross-language search for product IDs
    const searchResult = await sdk.client.fetch<{
      product_ids: string[]
    }>(`/store/products/search`, {
      method: "GET",
      query: {
        q: query,
        limit,
      },
      headers,
      next,
      cache: "no-store",
    })

    const productIds = searchResult.product_ids

    if (!productIds || productIds.length === 0) {
      return { products: [], count: 0 }
    }

    // Step 2: Fetch full products by IDs (with rental enrichment, calculated prices)
    const response = await sdk.client.fetch<{
      products: HttpTypes.StoreProduct[]
      count: number
    }>(`/store/products`, {
      method: "GET",
      query: {
        id: productIds,
        limit,
        region_id: region.id,
        locale,
        fields:
          "*variants.calculated_price,+variants.inventory_quantity,*variants.images,+metadata,+tags",
      },
      headers,
      next,
      cache: "no-store",
    })

    return {
      products: response.products || [],
      count: response.count || 0,
    }
  } catch (error) {
    console.error("Search error:", error)

    // Fallback: standard Medusa search (single-language)
    try {
      const response = await sdk.client.fetch<{
        products: HttpTypes.StoreProduct[]
        count: number
      }>(`/store/products`, {
        method: "GET",
        query: {
          q: query,
          limit,
          region_id: region.id,
          locale,
          fields:
            "*variants.calculated_price,+variants.inventory_quantity,*variants.images,+metadata,+tags",
        },
        headers,
        next,
        cache: "no-store",
      })

      return {
        products: response.products || [],
        count: response.count || 0,
      }
    } catch (fallbackError) {
      console.error("Fallback search error:", fallbackError)
      return { products: [], count: 0 }
    }
  }
}

/**
 * Revalidate search cache
 */
export async function revalidateSearch() {
  revalidateTag("products", "max")
}
